'use client'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'

// Shared mechanics for the compare-page tooltip bubbles (InfoTooltip,
// FormFactorTooltip): tap-to-toggle with click-outside dismissal, plus
// edge-aware alignment. Closed bubbles are removed from layout so WebKit
// cannot count their untransformed bounds as document overflow. Measure on
// opening and resize, when the bubble has its final stylesheet and font size.
export const useTooltipBubble = (align: 'center' | 'right' | 'left' = 'center') => {
  const [open, setOpen] = useState(false)
  const [edgeAlign, setEdgeAlign] = useState(align)
  const ref = useRef<HTMLSpanElement>(null)
  const bubbleRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  useLayoutEffect(() => {
    if (!open) {
      return
    }
    const measure = () => {
      if (!ref.current || !bubbleRef.current) {
        return
      }
      const trigger = ref.current.getBoundingClientRect()
      const half = bubbleRef.current.offsetWidth / 2
      const centerX = (trigger.left + trigger.right) / 2
      const pageWidth = document.documentElement.clientWidth
      setEdgeAlign(centerX + half > pageWidth - 8 ? 'right' : centerX - half < 8 ? 'left' : align)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [open, align])

  const alignClass = edgeAlign === 'right' ? 'right-0' : edgeAlign === 'left' ? 'left-0' : 'left-1/2 -translate-x-1/2'

  return { open, setOpen, ref, bubbleRef, alignClass }
}
